import {
  PolarisApiCitationDto,
  PolarisApiMessageDto,
  PolarisCitation,
  PolarisModelVersion,
  PolarisTurnStatus
} from '../polaris/model';
import {ISO_DATE_ONLY_REGEX} from '../polaris/polaris.utils';

export function toStrictDateRange(dateRange: unknown): {from: string; to: string} | undefined {
  if (!dateRange) return undefined;
  if (typeof dateRange === 'string') {
    throw new Error(`Invalid dateRange: expected object, got string "${dateRange}".`);
  }
  const range = dateRange as {from?: unknown; to?: unknown};
  if (typeof range.from !== 'string' || typeof range.to !== 'string') return undefined;
  assertIsoDateOnly(range.from, 'from');
  assertIsoDateOnly(range.to, 'to');
  return {from: range.from, to: range.to};
}

export function assertIsoDateOnly(value: string, field: 'from' | 'to'): void {
  if (!ISO_DATE_ONLY_REGEX.test(value)) {
  throw new Error(`Invalid dateRange.${field}: expected YYYY-MM-DD, got "${value}".`);
}
const [y, m, d] = value.split('-').map(Number);
const parsed = new Date(Date.UTC(y, m - 1, d));
if (
  Number.isNaN(parsed.getTime()) ||
  parsed.getUTCFullYear() !== y ||
  parsed.getUTCMonth() + 1 !== m ||
  parsed.getUTCDate() !== d
) {
  throw new Error(`Invalid dateRange.${field}: "${value}" is not a real calendar date.`);
}
}

export function toTurnStatus(status?: string): PolarisTurnStatus {
  switch ((status ?? '').toLowerCase()) {
    case 'success':
    case 'clarification_needed':
      return 'completed';
    case 'error':
      return 'failed';
    case 'in_progress':
      return 'pending';
    default:
      return 'completed';
  }
}

export function toDomainModelVersion(value: string | undefined): PolarisModelVersion | undefined {
  return value === 'v1' || value === 'v2' ? value : undefined;
}

export function toDomainCitations(input: PolarisApiCitationDto[] | undefined): PolarisCitation[] | undefined {
  if (!Array.isArray(input) || input.length === 0) return undefined;
  return input.filter(c => isSafeUrl(c.url));
}

export function isSafeUrl(url: string): boolean {
  try {
    const {protocol} = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

export function normalizeCompetitorsKeys(keys: string[] | string | undefined): string[] | undefined {
  if (keys == null) return undefined;
  if (Array.isArray(keys)) return keys.length ? keys : undefined;
  const trimmed = keys.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const filtered = parsed.filter((item): item is string => typeof item === 'string');
        return filtered.length ? filtered : undefined;
      }
    } catch { /* fall through to CSV */ }
  }
  const fromCsv = trimmed.split(',').map(item => item.trim()).filter(Boolean);
  return fromCsv.length ? fromCsv : undefined;
}

export function toDomainCompetitorsKeys(keys: string | string[] | undefined): string[] | undefined {
  return normalizeCompetitorsKeys(keys);
}

export function toInsights(input: string[] | string | null | undefined): string[] | null {
  if (Array.isArray(input)) return input.filter((item): item is string => typeof item === 'string');
  if (typeof input === 'string' && input.trim()) return [input];
  return null;
}

export function toStringArray(input: unknown): string[] {
  return Array.isArray(input) ? input.filter((item): item is string => typeof item === 'string') : [];
}

export function resolveUserTimestamp(dto: PolarisApiMessageDto, fallback?: number): number {
  const legacyTs = getLegacyTimestamp(dto);
  const defaultTs = normalizeTimestamp(fallback, Date.now());
  return normalizeTimestamp(dto.userTimestamp, normalizeTimestamp(legacyTs, defaultTs));
}

export function resolveSystemTimestamp(dto: PolarisApiMessageDto): number | null {
  const candidate = dto.author?.toLowerCase() === 'assistant'
    ? dto.userTimestamp
    : getLegacyTimestamp(dto);
  return normalizeTimestamp(candidate, null);
}

function getLegacyTimestamp(dto: PolarisApiMessageDto): number | undefined {
  return (dto as PolarisApiMessageDto & {timestamp?: number}).timestamp;
}

export function getLegacyStatus(dto: PolarisApiMessageDto): string | undefined {
  return (dto as PolarisApiMessageDto & {status?: string}).status;
}

export function getLegacyPolarisLambdaRequestId(dto: PolarisApiMessageDto): string | null {
  return (dto as PolarisApiMessageDto & {polarisLambdaRequestId?: string | null}).polarisLambdaRequestId ?? null;
}

export function normalizeTimestamp(value: unknown, fallback: number): number;
export function normalizeTimestamp(value: unknown, fallback: null): number | null;
export function normalizeTimestamp(value: unknown, fallback: number | null): number | null {
  if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) return fallback;
  return value;
}
